import { CatalogError, type ArtifactEntry, type ClientIdentity, type Manifest } from "./types";

// Structural check of manifest.schema.json (v1) for the browser, plus the
// cross-field rules the builder guarantees. A manifest that fails any rule is
// rejected as a whole; nothing from it is used.

export const NAMESPACES = ["common", "charging", "billing", "operator", "public", "notifications"] as const;

const LANGUAGE = /^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$/;
const REVISION = /^[0-9a-f]{16}$/;
const SHA256 = /^[0-9a-f]{64}$/;
const CLIENT = /^[a-z][a-z0-9-]+$/;
const VERSION_KEY = /^[1-9][0-9]*$/;
const ARTIFACT_PATH = /^artifacts\/([a-z]{2,3}(?:-[A-Za-z0-9]+)*)\/([a-z_]+)\.([0-9a-f]{12})\.json$/;
const MANIFEST_KEYS = ["schema_version", "catalog_revision", "min_client_contract", "verified_contracts", "contract_snapshots", "base_language", "languages", "fallback", "clients"];

const isLanguage = (v: unknown): v is string => typeof v === "string" && LANGUAGE.test(v);
const isNamespace = (v: string) => (NAMESPACES as readonly string[]).includes(v);
const isContractVersion = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) >= 1;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function fail(detail: string): never {
  throw new CatalogError("manifest_invalid", detail);
}

function uniqueArray(v: unknown, what: string): unknown[] {
  if (!Array.isArray(v) || new Set(v).size !== v.length) fail(`${what} must be an array of unique items`);
  return v;
}

/** Checks one artifact entry; its path must name exactly this language, namespace and content hash. */
export function parseArtifactEntry(v: unknown, language: string, namespace: string): ArtifactEntry {
  if (!isRecord(v) || Object.keys(v).sort().join() !== "bytes,path,sha256") fail(`artifact ${language}/${namespace} has unexpected fields`);
  const { path, sha256, bytes } = v;
  if (typeof sha256 !== "string" || !SHA256.test(sha256)) fail(`artifact ${language}/${namespace} sha256`);
  if (!Number.isSafeInteger(bytes) || (bytes as number) < 2) fail(`artifact ${language}/${namespace} bytes`);
  const m = typeof path === "string" ? ARTIFACT_PATH.exec(path) : null;
  if (!m || m[1] !== language || m[2] !== namespace || m[3] !== sha256.slice(0, 12)) fail(`artifact ${language}/${namespace} path`);
  return { path: path as string, sha256, bytes: bytes as number };
}

/** Published languages and their explicit fallback chains, each ending in the base language. */
export function parseLanguages(rawLanguages: unknown, rawFallback: unknown, base: string): { languages: string[]; fallback: Record<string, string[]> } {
  const languages = uniqueArray(rawLanguages, "languages");
  if (languages.length === 0 || !languages.every(isLanguage)) fail("languages");
  if (!languages.includes(base)) fail("base_language is not published");
  if (!isRecord(rawFallback)) fail("fallback");
  const fallback: Record<string, string[]> = {};
  for (const lang of languages as string[]) {
    const chain = uniqueArray(rawFallback[lang], `fallback.${lang}`);
    if (!chain.every((l) => typeof l === "string" && languages.includes(l) && l !== lang)) fail(`fallback.${lang} names an unpublished language`);
    if (lang === base ? chain.length !== 0 : chain[chain.length - 1] !== base) fail(`fallback.${lang} must end in ${base}`);
    fallback[lang] = chain as string[];
  }
  for (const lang of Object.keys(rawFallback)) if (!languages.includes(lang)) fail(`fallback.${lang} for an unpublished language`);
  return { languages: languages as string[], fallback };
}

/** Returns the manifest if it satisfies schema v1 and its cross-field rules; throws CatalogError otherwise. */
export function parseManifest(raw: unknown): Manifest {
  if (!isRecord(raw)) fail("not an object");
  const keys = Object.keys(raw);
  for (const k of keys) if (!MANIFEST_KEYS.includes(k)) fail(`unknown field ${k}`);
  for (const k of MANIFEST_KEYS) if (!(k in raw)) fail(`missing field ${k}`);
  if (raw.schema_version !== 1) fail("schema_version must be 1");
  if (typeof raw.catalog_revision !== "string" || !REVISION.test(raw.catalog_revision)) fail("catalog_revision");
  if (!isContractVersion(raw.min_client_contract)) fail("min_client_contract");
  if (!isLanguage(raw.base_language)) fail("base_language");
  const base = raw.base_language;

  const { languages, fallback } = parseLanguages(raw.languages, raw.fallback, base);

  if (!isRecord(raw.verified_contracts) || !isRecord(raw.contract_snapshots)) fail("verified_contracts / contract_snapshots");
  const verified: Record<string, number[]> = {};
  const snapshots: Record<string, Record<string, string>> = {};
  for (const [client, versions] of Object.entries(raw.verified_contracts)) {
    const list = uniqueArray(versions, `verified_contracts.${client}`);
    if (!CLIENT.test(client) || list.length === 0 || !list.every(isContractVersion)) fail(`verified_contracts.${client}`);
    const hashes = raw.contract_snapshots[client];
    if (!isRecord(hashes)) fail(`contract_snapshots.${client}`);
    const hashKeys = Object.keys(hashes);
    if (hashKeys.length !== list.length || !list.every((v) => hashKeys.includes(String(v)))) fail(`contract_snapshots.${client} must list exactly the verified versions`);
    for (const [v, h] of Object.entries(hashes)) if (!VERSION_KEY.test(v) || typeof h !== "string" || !SHA256.test(h)) fail(`contract_snapshots.${client}.${v}`);
    verified[client] = list as number[];
    snapshots[client] = hashes as Record<string, string>;
  }
  for (const client of Object.keys(raw.contract_snapshots)) if (!(client in verified)) fail(`contract_snapshots.${client} without verified_contracts`);

  if (!isRecord(raw.clients) || Object.keys(raw.clients).length === 0) fail("clients");
  const clients: Record<string, Record<string, Record<string, ArtifactEntry>>> = {};
  for (const [client, byLanguage] of Object.entries(raw.clients)) {
    if (!CLIENT.test(client) || !isRecord(byLanguage)) fail(`clients.${client}`);
    clients[client] = {};
    for (const [lang, byNamespace] of Object.entries(byLanguage)) {
      if (!languages.includes(lang) || !isRecord(byNamespace)) fail(`clients.${client}.${lang}`);
      clients[client][lang] = {};
      for (const [ns, entry] of Object.entries(byNamespace)) {
        if (!isNamespace(ns)) fail(`clients.${client}.${lang}.${ns} is not a namespace`);
        clients[client][lang][ns] = parseArtifactEntry(entry, lang, ns);
      }
    }
  }

  return {
    schema_version: 1,
    catalog_revision: raw.catalog_revision,
    min_client_contract: raw.min_client_contract,
    verified_contracts: verified,
    contract_snapshots: snapshots,
    base_language: base,
    languages,
    fallback,
    clients,
  };
}

/**
 * Throws contract_incompatible unless the release was verified for exactly
 * the contract version and snapshot this client was compiled with, and
 * delivers every declared namespace in the base language.
 * min_client_contract alone never proves compatibility.
 */
export function checkCompatibility(manifest: Manifest, identity: ClientIdentity): void {
  const { clientId, contractVersion, contractSha256 } = identity;
  if (!(manifest.verified_contracts[clientId] ?? []).includes(contractVersion)) {
    throw new CatalogError("contract_incompatible", `${manifest.catalog_revision} is not verified for ${clientId} v${contractVersion}`);
  }
  if (manifest.contract_snapshots[clientId]?.[String(contractVersion)] !== contractSha256) {
    throw new CatalogError("contract_incompatible", `${manifest.catalog_revision} verified a different ${clientId} v${contractVersion} snapshot`);
  }
  if (manifest.base_language !== identity.baseLanguage) {
    throw new CatalogError("contract_incompatible", `base language ${manifest.base_language} differs from the embedded ${identity.baseLanguage}`);
  }
  const base = manifest.clients[clientId]?.[manifest.base_language] ?? {};
  for (const ns of identity.namespaces) {
    if (!base[ns]) throw new CatalogError("contract_incompatible", `${manifest.catalog_revision} does not deliver ${manifest.base_language}/${ns} to ${clientId}`);
  }
}

/** Requested language first, then its explicit fallback chain (ending in the base language). */
export function languageChain(manifest: Pick<Manifest, "languages" | "fallback">, language: string): string[] {
  if (!manifest.languages.includes(language)) throw new CatalogError("language_unavailable", `${language} is not published`);
  return [language, ...(manifest.fallback[language] ?? [])];
}
